// src/pages/Casting/CastingCallDetailPage.tsx
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  CalendarClock,
  CheckCircle2,
  Globe,
  Loader2,
  MapPin,
  Users,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import { StudioErrorState } from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  EYEBROW,
  GHOST_BUTTON,
  INPUT,
  LABEL,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import castingService from '../../services/castingService';
import { StudioApiError, toDisplayMessage } from '../../services/studioApi';
import unifiedAuth from '../../utils/unifiedAuth';
import type { CastingCall } from '../../types/studio';

const COVER_NOTE_LIMIT = 4000;

function formatDeadline(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const CastingCallDetailPage = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const call = useStudioResource<CastingCall>(
    (signal) => castingService.getCall(slug, signal),
    [slug],
  );

  const data = call.data;
  const summary = data?.summary;

  const [applicantName, setApplicantName] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [location, setLocation] = useState('');
  const [coverNote, setCoverNote] = useState('');
  const [reelUrl, setReelUrl] = useState('');
  const [selfTapeUrl, setSelfTapeUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    // Sync fully with the server flag (not just set-true-and-forget): React
    // Router reuses this component across /casting/:slug param changes, so
    // without resetting to false here, applying to one call would keep
    // showing "Submission received" if the user later views a different
    // call they have not applied to.
    setApplied(Boolean(data?.hasApplied));
  }, [data?.hasApplied]);

  useSEO({
    title: summary ? `${summary.title} — Open Call at StrymHub` : 'Open Call — StrymHub Studios',
    description:
      summary?.roleSummary ||
      data?.description?.slice(0, 160) ||
      'An open casting call on a StrymHub Studios production.',
    keywords: summary
      ? `${summary.title}, casting call, ${summary.departmentLabel}, ${summary.location ?? 'uk'}, strymhub casting`
      : 'strymhub casting call',
    type: 'article',
  });

  const isAuthenticated = unifiedAuth.isAuthenticated();

  const handleApply = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!applicantName.trim() || !coverNote.trim()) {
      setSubmitError('We need your name and a short note about why you want this.');
      return;
    }

    setSubmitting(true);
    try {
      await castingService.apply(slug, {
        applicantName: applicantName.trim(),
        applicantPhone: applicantPhone.trim() || undefined,
        location: location.trim() || undefined,
        coverNote: coverNote.trim(),
        reelUrl: reelUrl.trim() || undefined,
        selfTapeUrl: selfTapeUrl.trim() || undefined,
      });
      setApplied(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error instanceof StudioApiError && error.isUnauthenticated) {
        navigate(`/login?redirect=/casting/${slug}`);
        return;
      }
      setSubmitError(toDisplayMessage(error, 'We could not submit your application.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (call.loading) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`} role="status" aria-label="Loading open call">
          <div className="h-4 w-32 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-8 h-12 w-2/3 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-6 space-y-3">
            <div className="h-4 animate-pulse rounded bg-white/[0.04]" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-white/[0.04]" />
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </StudioShell>
    );
  }

  if (call.error || !data || !summary) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`}>
          <StudioErrorState
            message={call.error ?? 'We could not find that open call.'}
            onRetry={call.reload}
          />
          <div className="mt-8 flex justify-center">
            <Link to="/casting" className={SECONDARY_BUTTON}>
              All open calls
            </Link>
          </div>
        </div>
      </StudioShell>
    );
  }

  const deadline = formatDeadline(summary.submissionDeadline);
  const closed = !summary.acceptingApplications;

  return (
    <StudioShell>
      <div className="border-b border-white/10">
        <div className={`${CONTAINER} py-12 sm:py-16`}>
          <Link to="/casting" className={GHOST_BUTTON}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All open calls
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-orange-500/30 bg-orange-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-200">
              {summary.callTypeLabel}
            </span>
            <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-400">
              {summary.departmentLabel}
            </span>
            {closed && (
              <span className="rounded-full border border-neutral-600/40 bg-neutral-700/20 px-3 py-1 text-xs text-neutral-400">
                Submissions closed
              </span>
            )}
          </div>

          <h1 className="heading-cinematic mt-5 max-w-3xl text-3xl text-white sm:text-4xl lg:text-5xl">
            {summary.title}
          </h1>

          {summary.productionSlug && summary.productionTitle && (
            <p className="mt-4 text-sm text-neutral-400">
              For{' '}
              <Link
                to={`/productions/${summary.productionSlug}`}
                className="font-semibold text-orange-300 underline-offset-4 hover:underline"
              >
                {summary.productionTitle}
              </Link>
            </p>
          )}

          {summary.roleSummary && (
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-300">
              {summary.roleSummary}
            </p>
          )}

          <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-400">
            {summary.location && (
              <div className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                <dt className="sr-only">Location</dt>
                <dd>{summary.location}</dd>
              </div>
            )}
            {summary.remoteFriendly && (
              <div className="inline-flex items-center gap-2">
                <Globe className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                <dt className="sr-only">Remote</dt>
                <dd>Remote friendly</dd>
              </div>
            )}
            <div className="inline-flex items-center gap-2">
              <Banknote className="h-4 w-4 text-neutral-500" aria-hidden="true" />
              <dt className="sr-only">Compensation</dt>
              <dd>{summary.compensationSummary || (summary.paid ? 'Paid' : 'Expenses only')}</dd>
            </div>
            {deadline && (
              <div className="inline-flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                <dt className="sr-only">Deadline</dt>
                <dd>Closes {deadline}</dd>
              </div>
            )}
            <div className="inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-neutral-500" aria-hidden="true" />
              <dt className="sr-only">Openings</dt>
              <dd>
                {summary.openings} {summary.openings === 1 ? 'place' : 'places'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={`${CONTAINER} py-14`}>
        <div className="grid gap-14 lg:grid-cols-[1fr_minmax(0,400px)]">
          <div className="space-y-12">
            {data.description && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">The brief</h2>
                <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-neutral-300">
                  {data.description}
                </p>
              </section>
            )}

            {data.requirements && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">What we need from you</h2>
                <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-neutral-300">
                  {data.requirements}
                </p>
              </section>
            )}

            {(summary.ageRange || summary.shootDatesSummary) && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">Details</h2>
                <dl className="mt-5 divide-y divide-white/[0.07] border-y border-white/[0.07]">
                  {summary.ageRange && (
                    <div className="flex gap-6 py-3.5 text-sm">
                      <dt className="w-40 shrink-0 text-neutral-500">Age range played</dt>
                      <dd className="text-neutral-200">{summary.ageRange}</dd>
                    </div>
                  )}
                  {summary.shootDatesSummary && (
                    <div className="flex gap-6 py-3.5 text-sm">
                      <dt className="w-40 shrink-0 text-neutral-500">Shoot dates</dt>
                      <dd className="text-neutral-200">{summary.shootDatesSummary}</dd>
                    </div>
                  )}
                </dl>
              </section>
            )}
          </div>

          {/* Apply */}
          <aside>
            <div className={`${PANEL} sticky top-24 p-6`}>
              {applied ? (
                <div className="text-center">
                  <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" aria-hidden="true" />
                  <p className="mt-4 font-display text-lg font-bold text-white">
                    Submission received
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                    We read every one. If you&apos;re right for it, we&apos;ll be in touch about
                    next steps.
                  </p>
                  <Link to="/casting/my-applications" className={`${SECONDARY_BUTTON} mt-6 w-full`}>
                    Track your applications
                  </Link>
                </div>
              ) : closed ? (
                <div className="text-center">
                  <p className="font-display text-lg font-bold text-white">
                    This call has closed
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                    Join the troupe and you&apos;ll be in the pool the moment the next one opens.
                  </p>
                  <Link to="/community/join" className={`${PRIMARY_BUTTON} mt-6 w-full`}>
                    Join the troupe
                  </Link>
                </div>
              ) : !isAuthenticated ? (
                <div className="text-center">
                  <p className="font-display text-lg font-bold text-white">Sign in to apply</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                    Applications are tied to your StrymHub account so you can track them and we can
                    reach you.
                  </p>
                  <Link
                    to={`/login?redirect=/casting/${slug}`}
                    className={`${PRIMARY_BUTTON} mt-6 w-full`}
                  >
                    Sign in
                  </Link>
                  <Link to="/signup" className={`${SECONDARY_BUTTON} mt-3 w-full`}>
                    Create an account
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleApply} className="space-y-4">
                  <h2 className={EYEBROW}>Apply for this</h2>

                  {submitError && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200"
                    >
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      <p>{submitError}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="applicantName" className={LABEL}>
                      Your name <span className="text-orange-400">*</span>
                    </label>
                    <input
                      id="applicantName"
                      required
                      value={applicantName}
                      onChange={(event) => setApplicantName(event.target.value)}
                      className={INPUT}
                    />
                  </div>

                  <div>
                    <label htmlFor="applicantLocation" className={LABEL}>
                      Where you&apos;re based
                    </label>
                    <input
                      id="applicantLocation"
                      value={location}
                      onChange={(event) => setLocation(event.target.value)}
                      className={INPUT}
                      placeholder="Sheffield, UK"
                    />
                  </div>

                  <div>
                    <label htmlFor="applicantPhone" className={LABEL}>
                      Phone
                    </label>
                    <input
                      id="applicantPhone"
                      type="tel"
                      value={applicantPhone}
                      onChange={(event) => setApplicantPhone(event.target.value)}
                      className={INPUT}
                    />
                  </div>

                  <div>
                    <label htmlFor="coverNote" className={LABEL}>
                      Why you <span className="text-orange-400">*</span>
                    </label>
                    <textarea
                      id="coverNote"
                      required
                      rows={6}
                      maxLength={COVER_NOTE_LIMIT}
                      value={coverNote}
                      onChange={(event) => setCoverNote(event.target.value)}
                      className={INPUT}
                      placeholder="A few honest lines. What draws you to this, and what you'd bring."
                    />
                    <p className="mt-1.5 text-xs text-neutral-500">
                      {coverNote.length}/{COVER_NOTE_LIMIT}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="reelUrl" className={LABEL}>
                      Showreel URL
                    </label>
                    <input
                      id="reelUrl"
                      type="url"
                      value={reelUrl}
                      onChange={(event) => setReelUrl(event.target.value)}
                      className={INPUT}
                      placeholder="https://…"
                    />
                  </div>

                  <div>
                    <label htmlFor="selfTapeUrl" className={LABEL}>
                      Self-tape URL
                    </label>
                    <input
                      id="selfTapeUrl"
                      type="url"
                      value={selfTapeUrl}
                      onChange={(event) => setSelfTapeUrl(event.target.value)}
                      className={INPUT}
                      placeholder="https://…"
                    />
                  </div>

                  <button type="submit" disabled={submitting} className={`${PRIMARY_BUTTON} w-full`}>
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Submit application
                  </button>

                  <p className="text-xs leading-relaxed text-neutral-500">
                    Your submission goes to the StrymHub casting team only. We never share it.
                  </p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </StudioShell>
  );
};

export default CastingCallDetailPage;
